import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, X, Star, Calendar, User, Volume2, Video, Music, Check, Search, Image } from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import { uploadAudio, uploadVideo, validateFile } from '../utils/cloudinaryUpload';
import '../styles/StoryEditPage.css';

const deriveName = (u) =>
  u ? (u.nickname || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || '—') : '—';

const formatDimensions = (a) => {
  const parts = [a?.width, a?.height, a?.depth].filter(Boolean);
  return parts.length ? parts.join(' × ') : null;
};

const thumbUrl = (url) => {
  if (!url) return null;
  if (url.includes('/upload/')) return url.replace('/upload/', '/upload/t_square-300/');
  return url;
};

const StoryEditPage = () => {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const [story, setStory] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    story_text: '',
    story_type: 'meaning',
    featured: false,
    audio_url: '',
    video_url: '',
    vc_id: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Artwork selection
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [showArtworkSearch, setShowArtworkSearch] = useState(false);
  const [artworkSearch, setArtworkSearch] = useState('');
  const [artworks, setArtworks] = useState([]);
  const [filteredArtworks, setFilteredArtworks] = useState([]);
  const [loadingArtworks, setLoadingArtworks] = useState(false);

  // Audio/Video upload state
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [audioFile, setAudioFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [audioPreview, setAudioPreview] = useState('');
  const [videoPreview, setVideoPreview] = useState('');

  const audioFileRef = useRef(null);
  const videoFileRef = useRef(null);

  useEffect(() => { loadStory(); }, [storyId]);

  // Filter artworks as user types
  useEffect(() => {
    if (!artworkSearch.trim()) { setFilteredArtworks(artworks); return; }
    const q = artworkSearch.toLowerCase();
    setFilteredArtworks(
      artworks.filter((a) =>
        (a.title || '').toLowerCase().includes(q) ||
        (deriveName(a.visual_artist)).toLowerCase().includes(q)
      )
    );
  }, [artworkSearch, artworks]);

  const loadStory = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('artspeaks_stories')
        .select(`
          story_id, title, story_text, story_type, featured, created_at, updated_at, audio_url, video_url, vc_id,
          story_writer:users!artist_uuid (uuid, nickname, first_name, last_name, email),
          artwork:vcs!vc_id (
            vc_id, title, cover_image, width, height, depth, description,
            visual_artist:users!uuid (uuid, nickname, first_name, last_name)
          ),
          event:events!event_id (id, name)
        `)
        .eq('story_id', storyId)
        .single();

      if (error) throw error;
      if (!data) { setError('Story not found'); return; }

      setStory(data);
      setFormData({
        title: data.title || '',
        story_text: data.story_text || '',
        story_type: data.story_type || 'meaning',
        featured: data.featured || false,
        audio_url: data.audio_url || '',
        video_url: data.video_url || '',
        vc_id: data.vc_id || ''
      });
      if (data.artwork) setSelectedArtwork(data.artwork);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadArtworks = useCallback(async () => {
    if (artworks.length > 0) return; // already loaded
    setLoadingArtworks(true);
    try {
      const { data: artworkData } = await supabase
        .from('vcs')
        .select('vc_id, title, cover_image, width, height, depth, description, uuid')
        .eq('access_control', 'Public')
        .not('title', 'is', null)
        .order('title', { ascending: true })
        .limit(500);

      const uuids = [...new Set((artworkData || []).map((a) => a.uuid).filter(Boolean))];
      let artistMap = new Map();
      if (uuids.length) {
        const { data: artists } = await supabase
          .from('users')
          .select('uuid, first_name, last_name, nickname')
          .in('uuid', uuids);
        (artists || []).forEach((a) => artistMap.set(a.uuid, a));
      }

      const processed = (artworkData || []).map((a) => ({
        ...a,
        visual_artist: a.uuid ? artistMap.get(a.uuid) || null : null
      }));
      setArtworks(processed);
      setFilteredArtworks(processed);
    } catch (e) {
      console.error('Error loading artworks:', e);
    } finally {
      setLoadingArtworks(false);
    }
  }, [artworks.length]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleArtworkSelect = (artwork) => {
    setSelectedArtwork(artwork);
    setFormData((prev) => ({ ...prev, vc_id: artwork.vc_id }));
    setShowArtworkSearch(false);
    setArtworkSearch('');
  };

  const handleFileSelect = (file, type) => {
    const validation = validateFile(file, type);
    if (!validation.isValid) { alert(validation.error); return; }
    if (type === 'audio') {
      setAudioFile(file);
      setAudioPreview(URL.createObjectURL(file));
      setFormData((prev) => ({ ...prev, audio_url: '' }));
    } else {
      setVideoFile(file);
      setVideoPreview(URL.createObjectURL(file));
      setFormData((prev) => ({ ...prev, video_url: '' }));
    }
  };

  const handleFileUpload = async (type) => {
    try {
      if (type === 'audio' && audioFile) {
        setUploadingAudio(true);
        const result = await uploadAudio(audioFile);
        setFormData((prev) => ({ ...prev, audio_url: result.url }));
        setAudioFile(null); setAudioPreview('');
      } else if (type === 'video' && videoFile) {
        setUploadingVideo(true);
        const result = await uploadVideo(videoFile);
        setFormData((prev) => ({ ...prev, video_url: result.url }));
        setVideoFile(null); setVideoPreview('');
      }
    } catch (err) {
      alert(`Failed to upload ${type}: ${err.message}`);
    } finally {
      setUploadingAudio(false);
      setUploadingVideo(false);
    }
  };

  const removeFile = (type) => {
    if (type === 'audio') {
      setAudioFile(null); setAudioPreview('');
      setFormData((prev) => ({ ...prev, audio_url: '' }));
    } else {
      setVideoFile(null); setVideoPreview('');
      setFormData((prev) => ({ ...prev, video_url: '' }));
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim()) { alert('Please enter a title'); return; }
    setSaving(true);
    try {
      const { error } = await supabase
        .from('artspeaks_stories')
        .update({
          title: formData.title,
          story_text: formData.story_text,
          story_type: formData.story_type,
          featured: formData.featured,
          audio_url: formData.audio_url || null,
          video_url: formData.video_url || null,
          vc_id: formData.vc_id || null,
          updated_at: new Date().toISOString()
        })
        .eq('story_id', storyId);

      if (error) throw error;
      alert('Story updated successfully!');
      navigate('/admin/art-stories');
    } catch (err) {
      alert('Failed to update story: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const goBack = () => navigate('/admin/art-stories');

  // ── Loading / error states ─────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="story-edit-page">
        <div className="loading-state">
          <div className="loading-spinner" />
          <p>Loading story…</p>
        </div>
      </div>
    );
  }

  if (error || !story) {
    return (
      <div className="story-edit-page">
        <div className="error-state">
          <h2>{error ? 'Error' : 'Story Not Found'}</h2>
          <p>{error || 'The requested story could not be found.'}</p>
          <button onClick={goBack} className="btn-primary">
            <ArrowLeft className="w-4 h-4" /> Go Back
          </button>
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="story-edit-page">
      {/* Header */}
      <div className="story-header">
        <div className="header-left">
          <button onClick={goBack} className="back-btn">
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="story-title-section">
            <h1>Edit Story</h1>
            <p className="story-subtitle">{story.title}</p>
          </div>
        </div>
        <div className="header-actions">
          <button onClick={goBack} className="btn-secondary">
            <X className="w-4 h-4" />
            Cancel
          </button>
          <button onClick={handleSave} className="btn-primary" disabled={saving}>
            <Save className="w-4 h-4" />
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Form */}
      <div className="story-edit-form">

        {/* ── Story Details ── */}
        <div className="form-section">
          <h3>Story Details</h3>

          <div className="form-group">
            <label htmlFor="title">Title *</label>
            <input
              type="text"
              id="title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="Enter story title"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="story_type">Story Type</label>
            <select
              id="story_type"
              value={formData.story_type}
              onChange={(e) => handleInputChange('story_type', e.target.value)}
              className="form-select"
            >
              <option value="meaning">Meaning — What the work means</option>
              <option value="inspiration">Inspiration — What inspired the work</option>
              <option value="process">Process — How the work was created</option>
              <option value="technique">Technique — Technical aspects</option>
              <option value="personal">Personal — Personal connection to the work</option>
              <option value="collaboration">Collaboration — Working with others</option>
            </select>
          </div>

          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.featured}
                onChange={(e) => handleInputChange('featured', e.target.checked)}
                className="form-checkbox"
              />
              <Star className="w-4 h-4" />
              Featured Story
            </label>
          </div>
        </div>

        {/* ── Artwork Card ── */}
        <div className="form-section">
          <h3>Artwork</h3>

          {selectedArtwork && !showArtworkSearch ? (
            <div className="artwork-card">
              <div className="artwork-card-thumb">
                {selectedArtwork.cover_image
                  ? <img src={thumbUrl(selectedArtwork.cover_image)} alt={selectedArtwork.title} />
                  : <div className="artwork-card-thumb-placeholder"><Image size={28} /></div>
                }
              </div>
              <div className="artwork-card-info">
                <div className="artwork-card-title">{selectedArtwork.title}</div>
                {selectedArtwork.visual_artist && (
                  <div className="artwork-card-artist">by {deriveName(selectedArtwork.visual_artist)}</div>
                )}
                {formatDimensions(selectedArtwork) && (
                  <div className="artwork-card-meta">{formatDimensions(selectedArtwork)}</div>
                )}
                {selectedArtwork.description && (
                  <div className="artwork-card-meta">{selectedArtwork.description}</div>
                )}
              </div>
              <button
                type="button"
                className="btn-text artwork-card-change"
                onClick={() => {
                  setShowArtworkSearch(true);
                  setArtworkSearch('');
                  loadArtworks();
                }}
              >
                Change
              </button>
            </div>
          ) : (
            <div className="artwork-search-panel">
              {selectedArtwork && (
                <button
                  type="button"
                  className="btn-text"
                  style={{ marginBottom: 10, fontSize: 13 }}
                  onClick={() => setShowArtworkSearch(false)}
                >
                  ← Keep current artwork
                </button>
              )}
              <div style={{ position: 'relative' }}>
                <Search style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', width: 16, height: 16, pointerEvents: 'none' }} />
                <input
                  type="text"
                  value={artworkSearch}
                  onChange={(e) => { setArtworkSearch(e.target.value); loadArtworks(); }}
                  placeholder="Search artworks by title or artist…"
                  className="form-input"
                  style={{ paddingLeft: 38 }}
                  autoFocus
                />
                {artworkSearch && (
                  <button type="button" onClick={() => setArtworkSearch('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}>
                    <X size={14} />
                  </button>
                )}
              </div>

              {loadingArtworks && <p style={{ color: '#9ca3af', fontSize: 13, marginTop: 8 }}>Loading artworks…</p>}

              {!loadingArtworks && artworkSearch && filteredArtworks.length > 0 && (
                <div className="artwork-dropdown">
                  {filteredArtworks.slice(0, 10).map((artwork) => (
                    <button
                      key={artwork.vc_id}
                      type="button"
                      className="artwork-option"
                      onClick={() => handleArtworkSelect(artwork)}
                    >
                      {artwork.cover_image && (
                        <img
                          src={thumbUrl(artwork.cover_image)}
                          alt={artwork.title}
                          style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{artwork.title}</div>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>{deriveName(artwork.visual_artist)}</div>
                        {formatDimensions(artwork) && <div style={{ fontSize: 11, color: '#9ca3af' }}>{formatDimensions(artwork)}</div>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Story Content ── */}
        <div className="form-section">
          <h3>Story Content</h3>
          <div className="form-group">
            <label htmlFor="story_text">Story Text</label>
            <textarea
              id="story_text"
              value={formData.story_text}
              onChange={(e) => handleInputChange('story_text', e.target.value)}
              placeholder="Enter the story content…"
              className="form-textarea"
              rows={10}
            />
          </div>
        </div>

        {/* ── Media ── */}
        <div className="form-section">
          <h3>Media</h3>

          {/* Audio */}
          <div className="form-group">
            <label>Audio Story</label>
            {formData.audio_url ? (
              <div className="uploaded-media-panel">
                <div className="uploaded-header"><Check className="check-icon" size={16} /><span>Audio Uploaded</span></div>
                <audio controls className="audio-preview"><source src={formData.audio_url} /></audio>
                <button type="button" onClick={() => removeFile('audio')} className="btn-text" style={{ marginTop: '0.5rem' }}>Remove Audio</button>
              </div>
            ) : (
              <div className="media-upload-container">
                <input ref={audioFileRef} type="file" accept="audio/*" onChange={(e) => handleFileSelect(e.target.files[0], 'audio')} style={{ display: 'none' }} />
                {!audioFile ? (
                  <div className="upload-area">
                    <button type="button" onClick={() => audioFileRef.current?.click()} className="upload-btn">
                      <Music className="w-4 h-4" /> Choose Audio File
                    </button>
                    <span className="upload-hint">MP3, WAV, M4A, AAC up to 50MB</span>
                  </div>
                ) : (
                  <>
                    <div className="file-preview">
                      <div className="file-info"><Volume2 className="w-4 h-4" /><span>{audioFile.name}</span></div>
                      <div className="file-actions">
                        <button type="button" onClick={() => handleFileUpload('audio')} disabled={uploadingAudio} className="btn-primary btn-sm">
                          {uploadingAudio ? 'Uploading…' : 'Upload to Cloudinary'}
                        </button>
                        <button type="button" onClick={() => removeFile('audio')} className="btn-secondary btn-sm">Cancel</button>
                      </div>
                    </div>
                    {audioPreview && <audio controls className="audio-preview"><source src={audioPreview} /></audio>}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Video */}
          <div className="form-group">
            <label>Video Story</label>
            <div className="media-upload-container">
              <input ref={videoFileRef} type="file" accept="video/*" onChange={(e) => handleFileSelect(e.target.files[0], 'video')} style={{ display: 'none' }} />
              {!videoFile && !formData.video_url && (
                <div className="upload-area">
                  <button type="button" onClick={() => videoFileRef.current?.click()} className="upload-btn">
                    <Video className="w-4 h-4" /> Choose Video File
                  </button>
                  <span className="upload-hint">MP4, MOV, AVI up to 100MB</span>
                </div>
              )}
              {videoFile && (
                <div className="file-preview">
                  <div className="file-info"><Video className="w-4 h-4" /><span>{videoFile.name}</span></div>
                  <div className="file-actions">
                    <button type="button" onClick={() => handleFileUpload('video')} disabled={uploadingVideo} className="btn-primary btn-sm">
                      {uploadingVideo ? 'Uploading…' : 'Upload'}
                    </button>
                    <button type="button" onClick={() => removeFile('video')} className="btn-secondary btn-sm">Remove</button>
                  </div>
                </div>
              )}
              {videoPreview && <video controls className="video-preview"><source src={videoPreview} /></video>}
              {formData.video_url && !videoFile && (
                <div className="uploaded-media-panel">
                  <div className="uploaded-header"><Check className="check-icon" size={16} /><span>Video Uploaded</span></div>
                  <video controls className="video-preview" style={{ width: '100%', maxHeight: 300 }}><source src={formData.video_url} /></video>
                  <button type="button" onClick={() => handleInputChange('video_url', '')} className="btn-text" style={{ marginTop: '0.5rem' }}>Remove Video</button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Story Information (read-only meta) ── */}
        <div className="form-section">
          <h3>Story Information</h3>
          <div className="info-grid">
            <div className="info-item">
              <Calendar className="info-icon" />
              <div className="info-content">
                <span className="info-label">Created</span>
                <span className="info-value">{new Date(story.created_at).toLocaleDateString()}</span>
              </div>
            </div>
            <div className="info-item">
              <User className="info-icon" />
              <div className="info-content">
                <span className="info-label">Story Writer</span>
                <span className="info-value">{deriveName(story.story_writer)}</span>
              </div>
            </div>
            {story.event && (
              <div className="info-item">
                <Calendar className="info-icon" />
                <div className="info-content">
                  <span className="info-label">Event</span>
                  <span className="info-value">{story.event.name}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoryEditPage;
